import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import SEOHead from "@/components/SEOHead";
import { toast } from "sonner";
import { History, RotateCcw, CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function AdminImportHistory() {
  const [imports, setImports] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => api.get("/admin/mid/imports").then((r) => setImports(r.data.imports)).catch(() => setImports([]));
  useEffect(() => { load(); }, []);

  const rollback = async (id) => {
    if (!window.confirm("Roll back to the state before this import? This restores the previous MID data.")) return;
    setBusy(id);
    try {
      await api.post(`/admin/mid/imports/${id}/rollback`);
      toast.success("Rolled back successfully");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Rollback failed");
    } finally { setBusy(""); }
  };

  const fmt = (iso) => iso ? new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "—";

  return (
    <div data-testid="admin-import-history-page" className="space-y-6">
      <SEOHead title="Import History" />
      <div>
        <h1 className="font-display text-2xl font-bold text-slate-900 flex items-center gap-2"><History className="h-6 w-6 text-[#0B2A6B]" /> Import History</h1>
        <p className="text-sm text-slate-500">Every MID import with version, counts and one-click rollback.</p>
      </div>

      {!imports ? (
        <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-[#0B2A6B]" /></div>
      ) : imports.length === 0 ? (
        <div data-testid="history-empty" className="rounded-2xl border border-dashed border-slate-300 bg-white py-16 text-center text-slate-400">No imports yet. Upload a workbook from the MID Import page.</div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white shadow-sm">
          <table className="w-full min-w-[900px] text-left text-sm">
            <thead><tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <th className="p-4">Version</th><th className="p-4">File</th><th className="p-4">Status</th>
              <th className="p-4">Colleges</th><th className="p-4">Duration</th><th className="p-4">By</th>
              <th className="p-4">When</th><th className="p-4 text-right">Action</th>
            </tr></thead>
            <tbody>
              {imports.map((im) => (
                <tr key={im._id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50" data-testid={`import-row-${im.version}`}>
                  <td className="p-4 font-bold text-slate-900">v{im.version}</td>
                  <td className="p-4 text-slate-600 max-w-[220px] truncate">{im.filename}</td>
                  <td className="p-4">
                    {im.rolledBack
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-0.5 text-xs font-semibold text-slate-600"><RotateCcw className="h-3 w-3" /> Rolled back</span>
                      : im.status === "success"
                      ? <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-semibold text-emerald-700"><CheckCircle2 className="h-3 w-3" /> Success</span>
                      : <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-0.5 text-xs font-semibold text-red-700"><XCircle className="h-3 w-3" /> Failed</span>}
                  </td>
                  <td className="p-4 text-slate-600">{im.counts?.colleges ?? "—"}</td>
                  <td className="p-4 text-slate-600">{im.durationMs ? `${(im.durationMs/1000).toFixed(1)}s` : "—"}</td>
                  <td className="p-4 text-slate-600 max-w-[160px] truncate">{im.importedBy}</td>
                  <td className="p-4 text-slate-500">{fmt(im.finishedAt)}</td>
                  <td className="p-4 text-right">
                    {im.status === "success" && !im.rolledBack ? (
                      <button data-testid={`rollback-btn-${im.version}`} onClick={() => rollback(im._id)} disabled={busy === im._id}
                        className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-40">
                        {busy === im._id ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCcw className="h-4 w-4" />} Rollback
                      </button>
                    ) : <span className="text-xs text-slate-300">—</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
